/**
 * useClientCompat — keeps an old client build from talking to a newer backend.
 *
 * The live compatibility level is whatever the deployed build says in
 * `/build-manifest.json` (`{ buildId, compatLevel }`). This hook reads it on
 * start, when the page becomes visible, every 60 s while visible, on `online`
 * and when a callable is refused with `client-outdated` (`callFunction`
 * dispatches `sendly:client-outdated`). When this bundle's `COMPAT_LEVEL` is
 * below the live one it decides (docs/spec/19d-client-backend-compat.md,
 * "The client watches: useClientCompat"):
 *
 *   - offline, or the manifest is absent / unreadable → nothing;
 *   - inside the boot window, or on an unattended page (kiosk TV, spectator
 *     board, embed) → the forced update, no prompt;
 *   - mid-session on a supervised page → a non-dismissable modal whose one
 *     button runs the same forced update. Never an automatic reload there: a
 *     half-filled form is never torn away.
 *
 * The boot window and the path classification are NOT reimplemented here: the
 * boot script publishes `window.__sendlyBootDeadline` and
 * `window.__sendlyPathKind()` (docs/spec/01c-sw-update-flow.md), read at the
 * moment of each decision. A missing global means the modal — uncertain data
 * never pulls a page away.
 *
 * The module is deliberately light: `judgeQueue.js`, `useMAU.js` and
 * `shardedCounter.js` import `isClientOutdated()` from here, so this file sits
 * in the entry bundle. Everything it imports is already there.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { COMPAT_LEVEL, parseBuildManifest, isNewerBuildId } from "@sendly/shared";
import { buildId as BUILD_ID } from "../buildInfo";
import { logEvent, EVT } from "../analytics";
import { PUBLIC_DASHBOARD_RE } from "../utils/publicRoutes";
import { CLIENT_OUTDATED_EVENT, announceClientOutdated } from "../utils/callFunction";
import { sessionStore } from "../utils/safeStorage";

/**
 * Path kinds where nobody is there to press a button and a reload loses no
 * input. Deliberately NOT the boot script's `AUTO_ACTIVATE_KINDS` (kiosk only):
 * there the other branch is a dismissable banner, here it is a modal that
 * makes the update mandatory anyway.
 */
export const UNATTENDED_KINDS = Object.freeze(["kiosk", "spectator", "embed"]);

/** Relative on purpose: the page's own origin is the Hosting release it came from. */
export const MANIFEST_URL = "/build-manifest.json";
/** The cadence of the service worker's own update poll. */
export const POLL_MS = 60000;
/** sessionStorage record `{ n, first, level_from?, trigger?, method? }` — the brake and the GA4 marker. */
export const RELOAD_KEY = "sendly-compat-reload";
/** At most this many forced updates ('reloading') ... */
export const BRAKE_MAX = 2;
/** ... within this window; then the standing screen. */
export const BRAKE_WINDOW_MS = 10 * 60 * 1000;

// The boot script's "a new worker is waiting and offered" window event.
const SW_UPDATE_EVENT = "sw-update-available";
// Above the service worker's precache deadlines, so a healthy install finishes.
const INSTALL_WAIT_MS = 15000;
const ASK_WAITING_MS = 1000;
// getRegistration() is a promise with no deadline of its own; a wedged one
// would keep the update button busy forever, never reaching the guaranteed path.
const REGISTRATION_WAIT_MS = 3000;
// The hook's own SKIP_WAITING watchdog: not 'activating' by then → the guaranteed path.
const ACTIVATE_WATCHDOG_MS = 3000;
// A worker that did move to 'activating' reloads the page through the boot
// script's controllerchange handler. If this page is still alive this much
// later, that reload is not coming; take the guaranteed path rather than leave
// the modal on a busy button forever.
const RELOAD_GRACE_MS = 10000;
// A wedged connection (an iOS PWA waking onto a socket the OS tore down)
// neither completes nor fails a fetch; without a bound one hung read would stop
// every later check.
const MANIFEST_TIMEOUT_MS = 10000;
// The GA4 report waits for a boot that held this long (the same 5 s App.jsx
// waits before it calls a boot stable), and by then a gym route has usually
// resolved its gym id.
export const REPORT_DELAY_MS = 5000;

// ── The "outdated" flag ──
// Module-level, not React state: the background writers (judge queue, MAU,
// sharded counters) run outside the tree and read it at the moment they would
// write. It goes up the moment the hook decides "below the live level" — modal
// or forced update — and stays up until the page reloads (the only way out),
// except on an unattended page whose live level falls back again.
let outdated = false;

/** True once this bundle is known to be below the live compatibility level. */
export function isClientOutdated() {
  return outdated;
}

/** Test seam. Never called by app code. */
export function __setClientOutdatedForTests(value) {
  outdated = !!value;
}

// The hook announces its own decision on `window` (the spec's contract); this
// guard keeps its own listener from reading that echo as a refused callable.
let selfDispatching = false;

function announce(liveLevel) {
  selfDispatching = true;
  try {
    announceClientOutdated(liveLevel);
  } finally {
    selfDispatching = false;
  }
}

// ── Boot-script globals, read at decision time, never cached ──

function readPathKind() {
  try {
    return typeof window.__sendlyPathKind === "function" ? window.__sendlyPathKind() : null;
  } catch {
    return null;
  }
}

function readBootDeadline() {
  const d = typeof window !== "undefined" ? window.__sendlyBootDeadline : undefined;
  return typeof d === "number" && Number.isFinite(d) ? d : null;
}

// ── The manifest ──

function withDeadline(promise, ms) {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => { timer = setTimeout(() => reject(new Error("timeout")), ms); }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Fetch and classify the live manifest. Never throws: a network error or a
 * timeout is `status: null`, which the shared parser calls `unreadable`.
 * Also used by the /version page, so there is one reader in the client.
 *
 * @returns {Promise<{ kind: 'absent'|'unreadable'|'ok', manifest: { buildId: string, compatLevel: number }|null }>}
 */
export async function loadLiveManifest({ timeoutMs = MANIFEST_TIMEOUT_MS } = {}) {
  let status = null;
  let contentType = null;
  let text = null;
  try {
    const ctrl = typeof AbortController === "function" ? new AbortController() : null;
    const read = (async () => {
      const res = await fetch(MANIFEST_URL, { cache: "no-cache", ...(ctrl ? { signal: ctrl.signal } : {}) });
      const body = await res.text();
      return { res, body };
    })();
    let result;
    try {
      result = await withDeadline(read, timeoutMs);
    } catch (err) {
      try { if (ctrl) ctrl.abort(); } catch { /* already settled */ }
      throw err;
    }
    status = result.res.status;
    contentType = result.res.headers && typeof result.res.headers.get === "function"
      ? result.res.headers.get("content-type")
      : null;
    text = result.body;
  } catch {
    status = null;
  }
  return parseBuildManifest({ status, contentType, text });
}

// ── The sessionStorage record: the brake's counters and the GA4 marker ──

function readRecord() {
  const r = sessionStore.getJSON(RELOAD_KEY);
  return r && typeof r === "object" && !Array.isArray(r) ? r : null;
}

function writeRecord(rec) {
  return sessionStore.setJSON(RELOAD_KEY, rec);
}

function inBrakeWindow(rec, now) {
  return !!rec && typeof rec.first === "number" && now - rec.first < BRAKE_WINDOW_MS;
}

/**
 * The loop brake: `{ tripped, reopensAt }`. Tripped once BRAKE_MAX forced
 * updates ran inside one BRAKE_WINDOW_MS; `reopensAt` is when the window ends.
 */
export function brakeState(rec, now = Date.now()) {
  if (inBrakeWindow(rec, now) && (Number(rec.n) || 0) >= BRAKE_MAX) {
    return { tripped: true, reopensAt: rec.first + BRAKE_WINDOW_MS };
  }
  return { tripped: false, reopensAt: null };
}

// Counted ONLY when the recovery answered 'reloading': a refused call
// (cooldown, pending, offline) is not an attempt.
function countAttempt(now = Date.now()) {
  const rec = readRecord() || {};
  const keep = inBrakeWindow(rec, now);
  writeRecord({ ...rec, n: keep ? (Number(rec.n) || 0) + 1 : 1, first: keep ? rec.first : now });
}

// The GA4 marker rides in the same record: the recovery reloads in the same
// tab, so sessionStorage carries it to the new build, which reports it.
function writeMarker(marker) {
  const rec = readRecord() || {};
  writeRecord({ ...rec, ...marker });
}

function stripMarker(rec) {
  const { level_from: _from, trigger: _trigger, method: _method, ...counters } = rec;
  return counters;
}

function clearMarker() {
  const rec = readRecord();
  if (rec && ("level_from" in rec || "trigger" in rec || "method" in rec)) writeRecord(stripMarker(rec));
}

// ── Service-worker helpers (path 1) ──

function waitForState(worker, isDone, ms) {
  return new Promise((resolve) => {
    let finished = false;
    let timer = null;
    let onChange = null;
    const finish = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      try { worker.removeEventListener("statechange", onChange); } catch { /* gone */ }
      resolve(worker.state);
    };
    onChange = () => { if (isDone(worker.state)) finish(); };
    if (isDone(worker.state)) { finish(); return; }
    try { worker.addEventListener("statechange", onChange); } catch { /* no events: the timer decides */ }
    timer = setTimeout(finish, ms);
  });
}

/**
 * The watcher behind the hook — plain JS, so the decision table is testable
 * without React. `onChange(state)` receives
 * `{ view: null|'modal'|'stuck', busy, unattended, liveLevel, liveBuildId }`.
 */
export function createCompatWatcher({ ownLevel = COMPAT_LEVEL, ownBuildId = BUILD_ID, onChange = () => {} } = {}) {
  let state = { view: null, busy: false, unattended: false, liveLevel: null, liveBuildId: null };
  // What raised the outdated decision first: the manifest ('session') or a
  // refused callable ('callable'). Only the GA4 `trigger` reads it.
  let origin = "session";
  let running = false;   // a forced update is in flight
  let leaving = false;   // a reload is under way; nothing else to do in this page
  let started = false;
  let pollTimer = null;
  let retryTimer = null;
  let checking = null;

  function set(patch) {
    state = { ...state, ...patch };
    onChange(state);
  }

  function isUnattended() {
    return UNATTENDED_KINDS.includes(readPathKind());
  }

  // The decision table's "boot window or unattended" row. Both globals must be
  // there; either one missing → the modal branch.
  function mayForce() {
    const deadline = readBootDeadline();
    const kind = readPathKind();
    if (deadline === null || !kind) return false;
    return Date.now() < deadline || UNATTENDED_KINDS.includes(kind);
  }

  function triggerName() {
    const deadline = readBootDeadline();
    if (deadline !== null && Date.now() < deadline) return "boot";
    return origin === "callable" ? "callable" : "session";
  }

  function clearRetry() {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  }

  function scheduleRetry(ms, fn) {
    clearRetry();
    retryTimer = setTimeout(() => { retryTimer = null; fn(); }, Math.max(0, ms));
  }

  function showStuck(unattended) {
    set({ view: "stuck", unattended, busy: false });
    // An unattended page never keeps a terminal screen: once the brake window
    // is over, try again (the check also hides the screen when the live level
    // is no longer above ours).
    if (unattended) {
      const rec = readRecord();
      const reopensAt = inBrakeWindow(rec, Date.now()) ? rec.first + BRAKE_WINDOW_MS : Date.now() + BRAKE_WINDOW_MS;
      scheduleRetry(reopensAt - Date.now() + 1000, () => { check(); });
    }
  }

  function markOutdated(fromEvent) {
    if (outdated) return;
    outdated = true;
    if (fromEvent) origin = "callable";
    else announce(state.liveLevel);
  }

  function decide(fromEvent) {
    if (!started || leaving) return;
    markOutdated(fromEvent);
    if (running) return;
    // The supervised standing screen is terminal.
    if (state.view === "stuck" && !state.unattended) return;
    // A scheduled re-evaluation (after 'cooldown' / 'pending', or the end of
    // the brake window on an unattended page) owns the next step; a 60 s poll
    // or another refused call in between must not jump the queue.
    if (retryTimer) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    if (mayForce()) {
      // No sessionStorage = no brake that survives the reload, so an automatic
      // forced update could loop (the boot script's braked reload refuses for
      // the same reason). A user's tap on the modal button is still allowed.
      if (sessionStore.available()) { runForcedUpdate(); return; }
      if (isUnattended()) { showStuck(true); return; }
    }
    if (state.view !== "modal") set({ view: "modal", unattended: false, busy: false });
    // After the state change, never before it: the modal and its button must
    // not wait for anything the warm-up does.
    startWarming();
  }

  // ── Warming the waiting build while the modal is up ──
  // (docs/spec/01c-sw-update-flow.md, "Warming the waiting build"). The tap
  // activates the waiting worker, and its reload would download the new
  // startup bundle on the boot screen; asked now, the worker has it in its own
  // cache by then. Mandatory — no `optional` flag: the update cannot be
  // declined, so the bytes are downloaded at the tap anyway, Save-Data or not.
  // Fire-and-forget: a tap before it finished runs the forced update as
  // before, and whatever is cached already is a partial win.
  let warming = false;

  function warmWaiting(reg) {
    const warm = window.__sendlyWarmWaiting;
    if (typeof warm !== "function" || !reg || !reg.waiting) return false;
    try { warm(reg.waiting); } catch { /* the helper never throws; nothing to do if it did */ }
    return true;
  }

  // A worker that starts waiting later: the boot script dispatches this event
  // for every waiting worker it would offer (after its own optional warm
  // call, which the helper's once-per-worker guard turns this call into a
  // no-op for — except on Save-Data, where that call skipped and this is the
  // one that posts).
  function onUpdateAvailable() {
    if (!started || leaving) return;
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : null;
    if (!sw || typeof sw.getRegistration !== "function") return;
    Promise.resolve()
      .then(() => sw.getRegistration())
      .then((reg) => { if (started && !leaving) warmWaiting(reg); })
      .catch(() => { /* no registration: nothing to warm */ });
  }

  function startWarming() {
    if (warming) return;
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : null;
    // No helper: a dev host or a browser without service workers.
    if (typeof window.__sendlyWarmWaiting !== "function" || !sw || typeof sw.getRegistration !== "function") return;
    warming = true;
    // Listening before the registration is read, so a worker that finishes
    // installing in between is not missed.
    window.addEventListener(SW_UPDATE_EVENT, onUpdateAvailable);
    (async () => {
      let reg = null;
      try { reg = await sw.getRegistration(); } catch { reg = null; }
      if (!reg || !started || leaving) return;
      if (warmWaiting(reg)) return;
      // Nothing waiting yet: look for the new worker (best effort — offline or
      // a failed check just leaves the event listener to catch a later one).
      try { Promise.resolve(reg.update()).catch(() => {}); } catch { /* update() unavailable */ }
    })();
  }

  function onLevelOk() {
    // Only an unattended standing screen yields to a live level that fell back
    // (a Hosting rollback). The modal and the supervised screen stay until the
    // reload the user starts.
    if (state.view === "stuck" && state.unattended) {
      clearRetry();
      outdated = false;
      set({ view: null, busy: false });
    }
  }

  function check() {
    if (!started || leaving || !ownBuildId) return Promise.resolve();
    if (typeof navigator !== "undefined" && navigator.onLine === false) return Promise.resolve();
    if (checking) return checking;
    checking = (async () => {
      const res = await loadLiveManifest();
      if (!started || res.kind !== "ok") return;
      const live = res.manifest;
      set({ liveLevel: live.compatLevel, liveBuildId: live.buildId });
      if (ownLevel >= live.compatLevel) { onLevelOk(); return; }
      decide(false);
    })().finally(() => { checking = null; });
    return checking;
  }

  // Path 1 — a newer worker is (or becomes) waiting: activate it. Its
  // controllerchange reload (the boot script's handler) is a cache hit.
  // Resolves true when the worker moved to 'activating'.
  async function waitingWorkerPath(trigger) {
    const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : null;
    const ask = window.__sendlyAskWorkerBuildId;
    // Without a controller the boot script's controllerchange handler does not
    // reload (a page that started uncontrolled is not stale), so an activation
    // would update nothing visible: straight to the guaranteed path.
    if (!sw || !sw.controller || typeof sw.getRegistration !== "function" || typeof ask !== "function") return false;
    let reg = null;
    try { reg = await withDeadline(Promise.resolve(sw.getRegistration()), REGISTRATION_WAIT_MS); } catch { reg = null; }
    if (!reg) return false;
    try { await withDeadline(Promise.resolve(reg.update()), INSTALL_WAIT_MS); } catch { /* offline or slow: still look at a worker already waiting */ }
    if (reg.installing) {
      await waitForState(reg.installing, (s) => s !== "installing", INSTALL_WAIT_MS);
    }
    const waiting = reg.waiting;
    if (!waiting) return false;
    let id = null;
    try { id = await ask(waiting, ASK_WAITING_MS); } catch { id = null; }
    if (!isNewerBuildId(id, ownBuildId) || reg.waiting !== waiting) return false;
    writeMarker({ level_from: ownLevel, trigger, method: "waiting_sw" });
    // The boot script's one activation helper (docs/spec/01c-sw-update-flow.md,
    // "Worker activation"): once per worker, records the activation so a chunk
    // failure meanwhile waits for it. Its stuck-waiting watchdog stays off —
    // the state wait below is this path's own, and its fallback is path 2.
    const activate = window.__sendlyActivateWaiting;
    if (typeof activate === "function") {
      if (!activate(waiting, { watchdog: false, knownId: id })) return false;
    } else if (!waiting.__sendlySkipPosted) {
      // No helper: the boot script skips its service-worker block on local
      // hosts. The direct post, with the same once-per-worker flag.
      try {
        waiting.postMessage({ type: "SKIP_WAITING" });
        waiting.__sendlySkipPosted = true;
      } catch {
        return false;
      }
    }
    const s = await waitForState(waiting, (st) => st !== "installed", ACTIVATE_WATCHDOG_MS);
    return s === "activating" || s === "activated";
  }

  function handleStatus(result, unattended) {
    const status = result && result.status;
    const retryInMs = result && typeof result.retryInMs === "number" ? result.retryInMs : 1000;
    switch (status) {
      case "reloading":
        countAttempt();
        leaving = true;
        return;
      case "light-reload":
      case "waiting-sw":
        // Not reachable with cause 'forced-update' (it always takes the hard
        // path); a reload is under way either way, and these have their own brake.
        leaving = true;
        return;
      case "cooldown":
      case "pending":
        // Refused, not an attempt. Re-evaluate the decision table later: still
        // boot window / unattended → forced update again, otherwise the modal.
        clearMarker();
        scheduleRetry(retryInMs, () => decide(true));
        return;
      case "offline":
        // Nothing; the `online` listener runs the check again.
        clearMarker();
        return;
      default:
        // 'max-attempts', 'hardcap', or no recovery to call at all.
        clearMarker();
        showStuck(unattended);
    }
  }

  async function runForcedUpdate() {
    if (running || leaving) return;
    const unattended = isUnattended();
    if (brakeState(readRecord(), Date.now()).tripped) {
      showStuck(unattended);
      return;
    }
    running = true;
    set({ busy: true });
    const trigger = triggerName();
    try {
      if (await waitingWorkerPath(trigger)) {
        await new Promise((resolve) => { setTimeout(resolve, RELOAD_GRACE_MS); });
        if (!started) return;
      }
      // Path 2 — the guaranteed path: unregister, purge, `?_swr=` reload. A
      // plain location.reload() is never the fallback: under the navigation
      // stale-while-revalidate it would serve the cached old HTML.
      writeMarker({ level_from: ownLevel, trigger, method: "hard" });
      const recover = window.__sendlyChunkRecovery;
      let result = null;
      if (typeof recover === "function") {
        try { result = await recover(undefined, { cause: "forced-update" }); } catch { result = null; }
      }
      handleStatus(result, unattended);
    } finally {
      running = false;
      if (!leaving && state.busy) set({ busy: false });
    }
  }

  function onVisibility() {
    if (typeof document !== "undefined" && document.visibilityState === "visible") check();
  }
  function onOnline() { check(); }
  function onOutdatedEvent(e) {
    if (selfDispatching) return;
    const lvl = e && e.detail ? e.detail.liveLevel : undefined;
    if (typeof lvl === "number" && Number.isFinite(lvl)) set({ liveLevel: lvl });
    decide(true);
    // Fills in the live build id for the standing screen.
    check();
  }

  return {
    start() {
      if (started) return;
      // Dev build: the whole mechanism is off.
      if (!ownBuildId) return;
      started = true;
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("online", onOnline);
      window.addEventListener(CLIENT_OUTDATED_EVENT, onOutdatedEvent);
      pollTimer = setInterval(() => {
        if (document.visibilityState === "visible") check();
      }, POLL_MS);
      check();
    },
    stop() {
      if (!started) return;
      started = false;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.removeEventListener(CLIENT_OUTDATED_EVENT, onOutdatedEvent);
      clearInterval(pollTimer);
      pollTimer = null;
      clearRetry();
      window.removeEventListener(SW_UPDATE_EVENT, onUpdateAvailable);
      warming = false;
    },
    /** The modal's button. */
    update() {
      if (!started || running || leaving) return;
      runForcedUpdate();
    },
    check,
    getState: () => state,
  };
}

// ── GA4: client_forced_update (K8) ──
// Sent by the NEW build after a successful boot, from the marker the old build
// left in the record before its reload — never before the reload (an event
// sent before unload can be lost, and a failed attempt must not be logged).

let takenMarker; // undefined = not read yet in this page
let reported = false;

/**
 * Read the marker once per page and strip it from the record at once (the
 * brake's counters stay). Taken at mount, before the watcher can write a new
 * marker for a further forced update.
 */
export function takeForcedUpdateMarker() {
  if (takenMarker !== undefined) return takenMarker;
  const rec = readRecord();
  if (rec && ("level_from" in rec || "trigger" in rec || "method" in rec)) {
    takenMarker = { level_from: rec.level_from, trigger: rec.trigger, method: rec.method };
    writeRecord(stripMarker(rec));
  } else {
    takenMarker = null;
  }
  return takenMarker;
}

/**
 * The event's params for a marker, or null when nothing is to be sent: no
 * marker, no level rise, a public dashboard route or a non-'app' path kind (no
 * consent flow runs there).
 */
export function forcedUpdateEventParams(marker, { ownLevel = COMPAT_LEVEL, pathname, pathKind, gymId } = {}) {
  if (!marker || typeof marker.level_from !== "number") return null;
  if (!(ownLevel > marker.level_from)) return null;
  if (typeof pathname === "string" && PUBLIC_DASHBOARD_RE.test(pathname)) return null;
  if (pathKind !== "app") return null;
  const params = {
    trigger: marker.trigger,
    method: marker.method,
    level_from: marker.level_from,
    level_to: ownLevel,
  };
  if (gymId) params.gym_id = gymId;
  return params;
}

/** Send the event at most once per page. Returns whether it was sent. */
export function reportForcedUpdate({ ownLevel = COMPAT_LEVEL, gymId = null, log = logEvent } = {}) {
  if (reported) return false;
  const params = forcedUpdateEventParams(takeForcedUpdateMarker(), {
    ownLevel,
    pathname: typeof location !== "undefined" ? location.pathname : "",
    pathKind: readPathKind(),
    gymId,
  });
  if (!params) return false;
  reported = true;
  log(EVT.CLIENT_FORCED_UPDATE, params);
  return true;
}

/** Test seam: forget the per-page marker / report state. */
export function __resetCompatReportForTests() {
  takenMarker = undefined;
  reported = false;
}

const INITIAL_STATE = { view: null, busy: false, unattended: false, liveLevel: null, liveBuildId: null };

/**
 * Mounted once, at the root of `App`. Returns what the modal / standing screen
 * renders: `{ view, busy, unattended, ownLevel, ownBuildId, liveLevel,
 * liveBuildId, update }`.
 *
 * @param {{ gymId?: string|null }} [opts] the active gym, for the GA4 `gym_id`
 */
export function useClientCompat({ gymId = null } = {}) {
  const [state, setState] = useState(INITIAL_STATE);
  const watcherRef = useRef(null);
  const gymIdRef = useRef(gymId);
  gymIdRef.current = gymId;

  useEffect(() => {
    if (!BUILD_ID) return undefined;
    // Before the watcher starts: it may write a fresh marker for a further
    // forced update, which must not be mistaken for the one that brought us here.
    takeForcedUpdateMarker();
    const reportTimer = setTimeout(() => {
      reportForcedUpdate({ gymId: gymIdRef.current });
    }, REPORT_DELAY_MS);
    const watcher = createCompatWatcher({ onChange: setState });
    watcherRef.current = watcher;
    watcher.start();
    return () => {
      clearTimeout(reportTimer);
      watcher.stop();
      if (watcherRef.current === watcher) watcherRef.current = null;
    };
  }, []);

  const update = useCallback(() => {
    if (watcherRef.current) watcherRef.current.update();
  }, []);

  return { ...state, ownLevel: COMPAT_LEVEL, ownBuildId: BUILD_ID, update };
}
